import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { useAppProgressStore } from '@components/AppProgressOverlay/store/appProgressStore'
import { Avatar } from '@components/Avatar/Avatar'
import { ClipboardCopy } from '@components/ClipboardCopy/ClipboardCopy'
import { ConfirmLeaveWithSubscriptionCheck } from '@components/ConfirmLeaveModal/ConfirmLeaveWithSubscriptionCheck'
import { InstalledBotRow } from '@components/InstalledBotRow/InstalledBotRow'
import { useUploadAttachment } from '@components/MediaDropContext/useUploadAttachment'
import { SpaceTotalTips } from '@components/MessageLayout/tips/SpaceTotalTips'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { NodeOperatorIcon } from '@components/NodeOperatorIcon/NodeOperatorIcon'
import { popupToast } from '@components/Notifications/popupToast'
import { TownNotificationsButton } from '@components/NotificationSettings/NotificationsSettingsButton'
import { ContractInfoButtons } from '@components/Panel/ContractInfoButtons'
import { EditTownInfo } from '@components/Panel/EditTownInfo'
import { Panel } from '@components/Panel/Panel'
import { PanelButton } from '@components/Panel/PanelButton'
import { InteractiveSpaceIcon } from '@components/SpaceIcon'
import { minterRoleId } from '@components/SpaceSettingsPanel/rolePermissions.const'
import { RolesPanel } from '@components/SpaceSettingsPanel/RolesPanel'
import { SpaceSettingsNavigationPanel } from '@components/SpaceSettingsPanel/SpaceSettingsNavigationPanel'
import {
    fetchTipLeaderboard,
    queryKeyTipLeaderboard,
} from '@components/TipsLeaderboard/useTipLeaderboard'
import { TownInfoModal } from '@components/TownInfoModal/TownInfoModal'
import { TownPageV2 } from '@components/TownPage/TownPageV2'
import { EntitlementsDisplay } from '@components/TownPageLayout/EntitlementsDisplay'
import { ReviewsPanel } from '@components/TownReviews/ReviewsPanel'
import { TownReviews } from '@components/TownReviews/TownReviews'
import { LargeUploadImageTemplate } from '@components/UploadImage/LargeUploadImageTemplate'
import { UploadImageRequestConfig } from '@components/UploadImage/useOnImageChangeEvent'
import { useEthPrice } from '@components/Web3/useEthPrice'
import { openSeaAssetUrl } from '@components/Web3/utils'
import { useTreasuryBalances } from '@components/Web3/Wallet/TransferTreasury/useTreasuryBalances'
import { WalletLinkingPanel } from '@components/Web3/WalletLinkingPanel'
import { useQueryClient } from '@tanstack/react-query'
import { contractAddressFromSpaceId, isValidStreamId } from '@towns-protocol/sdk'
import { Address, Permission, SpaceAddressFromSpaceId } from '@towns-protocol/web3'
import { Box, FormRender, Icon, IconButton, Paragraph, Stack, Text, TextButton } from '@ui'
// eslint-disable-next-line no-restricted-imports
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { useBalance } from 'hooks/useBalance'
import { useDevice } from 'hooks/useDevice'
import { useEntitlements } from 'hooks/useEntitlements'
import { useEnvironment } from 'hooks/useEnvironment'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useCombinedEthAndUSDCInUSD } from 'hooks/useFormattedNormalizedTipAmount'
import { useFetchOperatorsQuery } from 'hooks/useOperatorData'
import { useRefreshSpaceMember } from 'hooks/useRefreshSpaceMember'
import { useSpaceChannels } from 'hooks/useSpaceChannels'
import { useWaitForInvalidation } from 'hooks/useWaitForInvalidation'
import { CHANNEL_INFO_PARAMS, PATHS, TOWN_INFO_PARAMS } from 'routes'
import { useUserStore } from 'store/userSettingsStore'
import { getInviteUrl, shortAddress } from 'ui/utils/utils'
import {
    LookupUser,
    useBaseReads,
    useConnectivity,
    useContractSpaceInfo,
    useGetRootKeyFromLinkedWallet,
    useHasPermission,
    useInstalledApps,
    useIsSpaceOwner,
    useMyUserId,
    useSpaceData,
    useSpaceDelegationOperator,
    useSpaceId,
    useSpaceMembers,
    useTownsClient,
    useTownsContext,
    useUser,
} from 'use-towns-client'
import { env } from 'utils'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

import { usePanelActions } from './layouts/hooks/usePanelActions'

export const SpaceInfoPanel = () => {
    return (
        <Panel label="Town Info" rightBarButton={<SpaceInfoHeaderActions />}>
            <SpaceInfo />
        </Panel>
    )
}

export const SpaceInfo = () => {
    const space = useSpaceData()
    const { isTouch } = useDevice()
    const [searchParams] = useSearchParams()

    // touch handles roles panel with modals
    const isRolesPanel = !isTouch && searchParams.get(CHANNEL_INFO_PARAMS.ROLES) != null
    const isSpaceSettingsPanel =
        !isTouch && searchParams.get(CHANNEL_INFO_PARAMS.SPACE_SETTINGS_NAVIGATION) != null
    const isBannedUsersPanel = !isTouch && searchParams.get(CHANNEL_INFO_PARAMS.BANNED) != null
    const { leaveRoom } = useTownsClient()
    const { casablancaClient } = useTownsContext()
    const channels = useSpaceChannels()
    const { loggedInWalletAddress } = useConnectivity()

    const { data: contractSpaceInfo } = useContractSpaceInfo(space?.id)
    const { enableSpaceMetadataV2 } = useFeatureFlags()
    const address = contractSpaceInfo?.address ?? ''
    const navigate = useNavigate()
    const { hasPermission: canEdit } = useHasPermission({
        spaceId: space?.id ?? '',
        walletAddress: loggedInWalletAddress ?? '',
        permission: Permission.ModifySpaceSettings,
    })

    const { data: myAbstractAccountAddress } = useMyAbstractAccountAddress()
    const { isOwner: myAbstractAccountIsOwner, ownerAddress: spaceOwnerAddress } = useIsSpaceOwner(
        space?.id,
        myAbstractAccountAddress,
    )

    const { hasPermission: canBan } = useHasPermission({
        spaceId: space?.id ?? '',
        walletAddress: loggedInWalletAddress ?? '',
        permission: Permission.ModifyBanning,
    })

    const { memberIds } = useSpaceMembers()
    const [activeModal, setActiveModal] = useState<
        | 'browse-channels'
        | 'members'
        | 'preview'
        | 'confirm-leave'
        | 'wallets'
        | 'edit-membership'
        | (typeof CHANNEL_INFO_PARAMS)[keyof typeof CHANNEL_INFO_PARAMS]
        | (typeof TOWN_INFO_PARAMS)[keyof typeof TOWN_INFO_PARAMS]
        | 'reviews'
        | undefined
    >(undefined)
    const [isLeavingTown, setIsLeavingTown] = useState(false)

    const { openPanel } = usePanelActions()

    const onShowBrowseChannels = useEvent(() => {
        if (isTouch) {
            setActiveModal(CHANNEL_INFO_PARAMS.BROWSE_CHANNELS)
        } else {
            openPanel(CHANNEL_INFO_PARAMS.BROWSE_CHANNELS)
        }
    })

    const onShowTownPreview = useEvent(() => {
        if (isTouch) {
            setActiveModal('preview')
            return
        }
        navigate(`/${PATHS.SPACES}/${spaceId}/${PATHS.PREVIEW}?rel=panel`)
    })

    const spaceId = useSpaceId()
    const onMembersClick = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.TOWN_MEMBERS)
    }, [openPanel])

    const onManageRolesClick = useEvent(() => {
        if (!isRolesPanel) {
            Analytics.getInstance().track('clicked on manage roles', {
                spaceId: spaceId,
            })
            openPanel(CHANNEL_INFO_PARAMS.ROLES)
            if (isTouch) {
                setActiveModal(CHANNEL_INFO_PARAMS.ROLES)
            }
        }
    })

    const onEditSpaceSettingsClick = useEvent(() => {
        if (!myAbstractAccountIsOwner) {
            return
        }
        if (!isSpaceSettingsPanel) {
            openPanel(CHANNEL_INFO_PARAMS.SPACE_SETTINGS_NAVIGATION)
            if (isTouch) {
                setActiveModal(CHANNEL_INFO_PARAMS.SPACE_SETTINGS_NAVIGATION)
            }
        }
    })

    const onEditTownMetadataClick = useEvent(() => {
        if (!myAbstractAccountIsOwner) {
            return
        }

        navigate(`/${PATHS.SPACES}/${spaceId}/${PATHS.EDIT_SPACE_METADATA}`)
    })

    const onBannedUsersClick = useEvent(() => {
        if (!isBannedUsersPanel) {
            openPanel(CHANNEL_INFO_PARAMS.BANNED)
            if (isTouch) {
                setActiveModal(CHANNEL_INFO_PARAMS.BANNED)
            }
        }
    })

    const onLeaveClick = useCallback(() => {
        setActiveModal('confirm-leave')
        Analytics.getInstance().track('clicked leave town', {
            spaceId,
            spaceName: space?.name,
        })
    }, [space?.name, spaceId])

    const setOptimisticSpaceInitialized = useAppProgressStore(
        (state) => state.setOptimisticSpaceInitialized,
    )
    const leaveTown = useCallback(async () => {
        if (!spaceId) {
            return
        }

        setIsLeavingTown(true)
        await leaveRoom(spaceId)

        // clean up
        setOptimisticSpaceInitialized(spaceId, false)

        Analytics.getInstance().track('confirmed leave town', {
            spaceId: spaceId,
        })

        setTimeout(() => {
            navigate('/')
        }, 1000)
    }, [leaveRoom, navigate, setOptimisticSpaceInitialized, spaceId])

    const onEditTownInfoClick = useCallback(() => {
        setActiveModal(TOWN_INFO_PARAMS.EDIT_TOWN_NAME)
    }, [setActiveModal])

    const setModalUndefined = useCallback(() => setActiveModal(undefined), [])

    const { uploadTownImageToStream } = useUploadAttachment()
    const isUploadingSpaceIconRef = useRef<boolean>(false)
    const { toast } = useRefreshSpaceMember(space?.id)

    const [spaceImageInvalidationId, setSpaceImageInvalidationId] = useState<string | undefined>()
    const [townMetadataInvalidationId, setTownMetadataInvalidationId] = useState<
        string | undefined
    >()

    useWaitForInvalidation(townMetadataInvalidationId, {
        onSuccess: () => popupToast(toast, { duration: Infinity }),
    })
    useWaitForInvalidation(spaceImageInvalidationId, {
        onSuccess: () => popupToast(toast, { duration: Infinity }),
    })

    const onUploadSpaceIcon = useCallback(
        async ({ file, id: spaceId, type, setProgress }: UploadImageRequestConfig) => {
            if (isUploadingSpaceIconRef.current) {
                return
            }

            if (type === 'spaceIcon' && isValidStreamId(spaceId) && casablancaClient) {
                isUploadingSpaceIconRef.current = true
                try {
                    // upload the space icon
                    const { invalidationId } = await uploadTownImageToStream(
                        spaceId,
                        file,
                        setProgress,
                    )
                    setSpaceImageInvalidationId(invalidationId)
                } finally {
                    isUploadingSpaceIconRef.current = false
                }
            }
        },
        [casablancaClient, uploadTownImageToStream],
    )

    return (
        <>
            {space?.id && (
                <Stack centerContent padding>
                    <FormRender>
                        {({ register, formState, setError, clearErrors }) => (
                            <LargeUploadImageTemplate
                                type="spaceIcon"
                                formFieldName="spaceIcon"
                                canEdit={Boolean(canEdit)}
                                resourceId={space.id}
                                setError={setError}
                                register={register}
                                formState={formState}
                                clearErrors={clearErrors}
                                imageRestrictions={{
                                    // no limits on dimensions for spaces
                                    minDimension: {
                                        message: '',
                                        min: 0,
                                    },
                                }}
                                onUploadImage={onUploadSpaceIcon}
                            >
                                <TownContractOpener address={address}>
                                    <InteractiveSpaceIcon
                                        spaceId={space.id}
                                        size="lg"
                                        spaceName={space.name}
                                        address={address}
                                    />
                                </TownContractOpener>
                            </LargeUploadImageTemplate>
                        )}
                    </FormRender>
                </Stack>
            )}
            <Stack gap>
                <TokensGatingSpace
                    spaceId={spaceId}
                    canEdit={!!myAbstractAccountIsOwner}
                    onClick={onEditSpaceSettingsClick}
                />
                <EditTownInfo
                    canEdit={myAbstractAccountIsOwner}
                    name={space?.name}
                    owner={spaceOwnerAddress}
                    address={address}
                    onEdit={onEditTownInfoClick}
                />
                <TownReviews
                    setActiveModal={(modal) => setActiveModal(modal === null ? undefined : modal)}
                    spaceId={spaceId}
                />
                <TownTreasury
                    address={contractSpaceInfo?.address}
                    myAbstractAccountIsOwner={!!myAbstractAccountIsOwner}
                />
                <TownTips spaceId={spaceId} />

                {!!spaceOwnerAddress && (
                    <TownOwner
                        address={contractSpaceInfo?.address}
                        tokenId={contractSpaceInfo?.tokenId}
                        owner={spaceOwnerAddress}
                        myAbstractAccountIsOwner={!!myAbstractAccountIsOwner}
                    />
                )}
                {!!spaceOwnerAddress && <EditDelegatedTo isOwner={!!myAbstractAccountIsOwner} />}
                {spaceId && <InstalledBots spaceId={spaceId} />}
                <PanelButton onClick={onShowTownPreview}>
                    <Icon type="search" size="square_sm" color="gray2" />
                    <Paragraph color="default">Preview Town Page</Paragraph>
                </PanelButton>
                {myAbstractAccountIsOwner && (
                    <PanelButton onClick={onEditSpaceSettingsClick}>
                        <Icon type="treasury" size="square_sm" color="gray2" />
                        <Paragraph color="default">Edit Membership Settings</Paragraph>
                    </PanelButton>
                )}
                {canEdit && enableSpaceMetadataV2 && !isTouch && (
                    <PanelButton onClick={onEditTownMetadataClick}>
                        <Icon type="edit" size="square_sm" color="gray2" />
                        <Paragraph color="default">Edit Town</Paragraph>
                    </PanelButton>
                )}

                {canEdit && (
                    <PanelButton onClick={onManageRolesClick}>
                        <Icon type="personEdit" size="square_sm" color="gray2" />
                        <Paragraph color="default">Manage Roles</Paragraph>
                    </PanelButton>
                )}
                <PanelButton onClick={onMembersClick}>
                    <Icon type="people" size="square_sm" color="gray2" />
                    <Paragraph color="default">
                        {`${memberIds.length} member${memberIds.length > 1 ? `s` : ``}`}
                    </Paragraph>
                </PanelButton>

                {spaceId && <TownNotificationsButton type="space" spaceId={spaceId} />}

                <PanelButton disabled={channels.length === 0} onClick={onShowBrowseChannels}>
                    <Icon type="tag" size="square_sm" color="gray2" />
                    <Paragraph color="default">
                        {`${channels.length} channel${channels.length != 1 ? `s` : ``}`}
                    </Paragraph>
                </PanelButton>

                {canBan && <BannedUsersPanelButton onClick={onBannedUsersClick} />}
                <PanelButton tone="negative" onClick={onLeaveClick}>
                    <Icon type="logout" size="square_sm" />
                    <Paragraph color="error">Leave {space?.name}</Paragraph>
                </PanelButton>
            </Stack>

            <Stack grow padding paddingBottom="lg" justifyContent="end">
                {/* footer content */}
            </Stack>

            {activeModal === 'confirm-leave' && (
                <ConfirmLeaveWithSubscriptionCheck
                    text={space?.name ? `Leave ${space.name}?` : 'Leave town?'}
                    isLoading={isLeavingTown}
                    onConfirm={leaveTown}
                    onCancel={setModalUndefined}
                />
            )}

            {activeModal === TOWN_INFO_PARAMS.EDIT_TOWN_NAME && (
                <TownInfoModal
                    setInvalidationId={setTownMetadataInvalidationId}
                    onHide={setModalUndefined}
                />
            )}

            {activeModal === 'preview' && (
                <ModalContainer padding="none" border="none" onHide={setModalUndefined}>
                    <Box
                        position="relative"
                        style={{
                            height: 'calc(100vh - 100px)',
                            width: isTouch ? '100%' : 'calc(100vw - 100px)',
                        }}
                    >
                        <TownPageV2 isPreview onClosePreview={setModalUndefined} />
                    </Box>
                </ModalContainer>
            )}

            {activeModal === 'wallets' && (
                <ModalContainer touchTitle="Wallets" onHide={setModalUndefined}>
                    <WalletLinkingPanel />
                </ModalContainer>
            )}

            {activeModal === 'space-settings' && (
                <ModalContainer touchTitle="Edit Me" onHide={setModalUndefined}>
                    <SpaceSettingsNavigationPanel />
                </ModalContainer>
            )}

            {isRolesPanel && <RolesPanel />}

            {activeModal === CHANNEL_INFO_PARAMS.ROLES && (
                <ModalContainer padding="none" border="none" onHide={setModalUndefined}>
                    <RolesPanel />
                </ModalContainer>
            )}

            {activeModal === 'reviews' && (
                <ModalContainer padding="none" border="none" onHide={setModalUndefined}>
                    <ReviewsPanel />
                </ModalContainer>
            )}
        </>
    )
}

const SpaceInfoHeaderActions = () => {
    const { isTouch } = useDevice()
    const space = useSpaceData()
    const spaceId = useSpaceId()
    const canUseWebShare =
        typeof navigator !== 'undefined' &&
        (typeof (navigator as Navigator & { share?: unknown }).share === 'function' ||
            'share' in (navigator as Navigator & { share?: unknown }))

    // Favorite handling
    const myUserId = useMyUserId()
    const [setFavoritedSpaces, favoritedSpaces] = useUserStore((s) => [
        s.setFavoriteSpaces,
        (myUserId && s.users[myUserId]?.favoriteSpaces) || [],
    ])
    const isFavorite = spaceId ? favoritedSpaces.includes(spaceId) : false

    const onToggleFavorite = useEvent(() => {
        if (!myUserId || !spaceId) {
            return
        }
        setFavoritedSpaces(
            myUserId,
            isFavorite
                ? favoritedSpaces.filter((f) => f !== spaceId)
                : [spaceId, ...favoritedSpaces],
        )
        const eventName = isFavorite
            ? 'clicked remove space from favorites'
            : 'clicked add space to favorites'
        Analytics.getInstance().track(eventName)
    })

    const onSharePressed = useEvent(async () => {
        if (!space) {
            return
        }
        if (!canUseWebShare) {
            return
        }
        const url = getInviteUrl({ spaceId: space.id })
        try {
            await navigator.share({ title: space.name, url: url, text: url })
        } catch (_) {
            // ignore
        }
    })

    return isTouch ? (
        <Stack horizontal gap="sm" paddingRight="xs" width="x8">
            <IconButton
                icon={isFavorite ? 'starFilled' : 'star'}
                background="none"
                color="default"
                onClick={onToggleFavorite}
            />
            {canUseWebShare && (
                <IconButton
                    icon="share"
                    background="none"
                    color="default"
                    onClick={onSharePressed}
                />
            )}
        </Stack>
    ) : (
        <></>
    )
}

const TownTreasury = (props: {
    address: string | undefined
    myAbstractAccountIsOwner: boolean
}) => {
    const { address, myAbstractAccountIsOwner } = props
    const { enableUsdc } = useFeatureFlags()
    const { data: balance } = useBalance({ address: address as Address, refetchInterval: 30_000 })
    const { currencies } = useTreasuryBalances({
        spaceAddress: address as Address,
        refetchInterval: 30_000,
        enabled: enableUsdc,
    })
    const ethAmount = currencies.find((c) => c.symbol === 'ETH')?.value ?? 0n
    const usdcAmount = currencies.find((c) => c.symbol === 'USDC')?.value ?? 0n
    const { data: ethPrice } = useEthPrice({
        enabled: enableUsdc,
    })
    const combinedAmountsInUsd = useCombinedEthAndUSDCInUSD(ethAmount, usdcAmount, ethPrice)

    const { openPanel } = usePanelActions()
    const openTransferTreasury = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.TRANSFER_TREASURY)
    }, [openPanel])

    if (!balance) {
        return null
    }

    return (
        <Stack padding="md" gap="sm" background="level2" rounded="sm">
            <Stack horizontal justifyContent="spaceBetween">
                <Paragraph strong color="default">
                    Town Treasury
                </Paragraph>
                {myAbstractAccountIsOwner && (
                    <TextButton onClick={openTransferTreasury}>Transfer</TextButton>
                )}
            </Stack>
            {enableUsdc ? (
                <Stack horizontal gap="sm" alignItems="center">
                    <Text as="span">{combinedAmountsInUsd}</Text>
                    <Icon type="usdc" />
                </Stack>
            ) : (
                <Stack horizontal gap="sm" alignItems="center">
                    <Text as="span">
                        {balance.formatted}{' '}
                        <Text strong display="inline" as="span">
                            ETH
                        </Text>
                    </Text>
                    <Icon type="base" color="coinbaseBlue" />
                </Stack>
            )}
        </Stack>
    )
}

const TownTips = (props: { spaceId: string | undefined }) => {
    const { spaceId } = props
    const { openPanel } = usePanelActions()
    const qc = useQueryClient()
    const { subgraphEndpoint } = useBaseReads()

    const prefetchTipLeaderboard = useCallback(() => {
        if (!spaceId || !qc) {
            return
        }
        const spaceAddress = SpaceAddressFromSpaceId(spaceId)
        qc.prefetchQuery({
            queryKey: queryKeyTipLeaderboard(spaceAddress),
            queryFn: () => fetchTipLeaderboard(spaceAddress, subgraphEndpoint),
        })
    }, [spaceId, qc, subgraphEndpoint])

    const onViewAll = useCallback(() => {
        openPanel('tips-leaderboard')
    }, [openPanel])

    if (!env.VITE_TIPS_ENABLED || !spaceId) {
        return null
    }

    return (
        <Stack gap="sm" padding="md" background="level2" rounded="sm">
            <Stack horizontal justifyContent="spaceBetween">
                <Paragraph strong color="default">
                    Tips
                </Paragraph>
                <TextButton onClick={onViewAll} onMouseEnter={prefetchTipLeaderboard}>
                    View All
                </TextButton>
            </Stack>
            <Box cursor="pointer" width="fit-content" onClick={onViewAll}>
                <SpaceTotalTips spaceId={spaceId} />
            </Box>
        </Stack>
    )
}

const TownOwner = (props: {
    owner: string
    address: string | undefined
    tokenId: string | undefined
    myAbstractAccountIsOwner: boolean
}) => {
    const { owner, address, tokenId, myAbstractAccountIsOwner } = props
    const { openPanel } = usePanelActions()
    const { data: ownerRootKeyAddress } = useGetRootKeyFromLinkedWallet({
        walletAddress: owner,
    })
    const openFounderPanel = useCallback(
        (ownerUser: LookupUser) => {
            if (ownerUser) {
                openPanel(CHANNEL_INFO_PARAMS.PROFILE, { profileId: ownerRootKeyAddress })
            }
        },
        [openPanel, ownerRootKeyAddress],
    )

    const { spaceDapp } = useTownsClient()
    const ownerContract = spaceDapp?.config.addresses.spaceOwner

    const openSeaNft: `0x${string}/${number}` | undefined = ownerContract
        ? `${ownerContract}/${Number(tokenId)}`
        : undefined

    const openTransferOwnership = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.TRANSFER_OWNERSHIP)
    }, [openPanel])

    // the owner in the contract is the smart account (or an EOA if transferred), we need to get the user id
    const { data: spaceOwnerUserId } = useGetRootKeyFromLinkedWallet({
        walletAddress: owner,
    })
    const ownerUser = useUser(spaceOwnerUserId)

    return (
        <Stack padding="md" gap="sm" background="level2" rounded="sm">
            <Stack horizontal justifyContent="spaceBetween">
                <Paragraph strong color="default">
                    Founder
                </Paragraph>
                {myAbstractAccountIsOwner && (
                    <TextButton onClick={openTransferOwnership}>Transfer</TextButton>
                )}
            </Stack>
            {ownerUser ? (
                <Box horizontal gap="sm">
                    <Box centerContent cursor="pointer" onClick={() => openFounderPanel(ownerUser)}>
                        {ownerUser && <Avatar size="avatar_x4" userId={ownerUser.userId} />}
                    </Box>
                    <Box overflow="hidden" paddingY="xs" gap="sm">
                        <Box cursor="pointer" onClick={() => openFounderPanel(ownerUser)}>
                            <Paragraph truncate data-testid="owner">
                                {ownerUser && getPrettyDisplayName(ownerUser)}
                            </Paragraph>
                        </Box>
                        {address && (
                            <ContractInfoButtons
                                ownerAddress={owner}
                                contractAddress={ownerContract ?? address}
                                nft={openSeaNft}
                            />
                        )}
                    </Box>
                </Box>
            ) : (
                <Paragraph color="gray2">{owner ? shortAddress(owner) : ''}</Paragraph>
            )}
        </Stack>
    )
}

const InstalledBots = (props: { spaceId: string }) => {
    const { spaceId } = props
    const { data: map } = useInstalledApps(spaceId ? SpaceAddressFromSpaceId(spaceId) : undefined)
    const installedApps = useMemo(() => Array.from(map?.values() ?? []), [map])
    const { openPanel } = usePanelActions()

    const openBotProfile = useCallback(
        (userId: string) => {
            if (userId) {
                openPanel(CHANNEL_INFO_PARAMS.PROFILE, { profileId: userId })
            }
        },
        [openPanel],
    )

    if (installedApps.length === 0) {
        return null
    }

    return (
        <Stack gap="sm" padding="md" background="level2" rounded="sm">
            <Paragraph strong color="default">
                Bots
            </Paragraph>
            <Stack gap background="level2" rounded="sm">
                {installedApps?.map(({ appId, appAddress }) => (
                    <InstalledBotRow
                        key={appId}
                        appAddress={appAddress}
                        openBotProfile={openBotProfile}
                    />
                ))}
            </Stack>
        </Stack>
    )
}

const TownContractOpener = (props: { address: string; children?: React.ReactNode }) => {
    const { address } = props
    const { baseChain } = useEnvironment()
    const chainId = baseChain.id
    const onClick = useCallback(() => {
        if (!chainId) {
            return
        }
        window.open(`${openSeaAssetUrl(chainId, address)}`, '_blank', 'noopener,noreferrer')
    }, [chainId, address])

    return (
        <Box padding="x4">
            <Box tooltip="View official town NFT" onClick={onClick}>
                <Box inset="md">{props.children}</Box>
            </Box>
        </Box>
    )
}

const BannedUsersPanelButton = (props: { onClick: () => void }) => {
    const { onClick } = props

    return (
        <PanelButton onClick={onClick}>
            <Icon type="ban" size="square_sm" color="negative" />
            <Paragraph color="error">Banned Users</Paragraph>
        </PanelButton>
    )
}

const TokensGatingSpace = ({
    spaceId,
    canEdit,
    onClick,
}: {
    spaceId: string | undefined
    canEdit: boolean
    onClick: () => void
}) => {
    const { data: entitlements } = useEntitlements(spaceId ?? '', minterRoleId)

    if (!entitlements.hasEntitlements) {
        return null
    }

    return (
        <Stack
            hoverable={canEdit}
            background="level2"
            padding="none"
            borderRadius="xl"
            cursor={canEdit ? 'pointer' : 'auto'}
            onClick={onClick}
        >
            <Box
                horizontal
                gap="sm"
                alignItems="center"
                paddingY="sm"
                paddingX="md"
                justifyContent="spaceBetween"
            >
                <Box horizontal centerContent gap="sm">
                    <Icon type="lock" size="square_sm" color="gray2" />
                    <Paragraph size="sm" color="default">
                        Gated
                    </Paragraph>
                </Box>
                <Box horizontal gap="sm" alignItems="center">
                    <Box>
                        <EntitlementsDisplay entitlements={entitlements} />
                    </Box>
                    {canEdit && (
                        <IconButton icon="edit" size="square_sm" color="gray2" onClick={onClick} />
                    )}
                </Box>
            </Box>
        </Stack>
    )
}

const EditDelegatedTo = (props: { isOwner: boolean }) => {
    const { isOwner } = props
    const { openPanel } = usePanelActions()

    const onClick = useCallback(() => {
        openPanel(CHANNEL_INFO_PARAMS.DELEGATE_TO_NODE_OPERATOR)
    }, [openPanel])

    const spaceId = useSpaceId()
    const spaceAddress = contractAddressFromSpaceId(spaceId ?? '') as `0x${string}`
    const { data: operatorAddress } = useSpaceDelegationOperator(spaceAddress)
    const { data: operators } = useFetchOperatorsQuery()
    const delegatedToOperator = operators?.operators.find(
        (operator) => operator.address.toLowerCase() === operatorAddress?.toLowerCase(),
    )
    const isEditable = !!delegatedToOperator && isOwner

    if (!delegatedToOperator) {
        return null
    }

    return (
        <Stack
            padding
            hoverable
            gap="paragraph"
            background="level2"
            borderRadius="sm"
            cursor={isEditable ? 'pointer' : 'auto'}
            onClick={isEditable ? onClick : undefined}
        >
            <Stack horizontal gap="sm">
                <Box grow>
                    <Paragraph fontWeight="strong" color="default">
                        Delegated to
                    </Paragraph>
                </Box>
                {!!isEditable && (
                    <Box alignContent="start">
                        <Paragraph color="gray2" size="sm">
                            Edit
                        </Paragraph>
                    </Box>
                )}
            </Stack>

            <Stack horizontal gap="sm">
                <Box>
                    <NodeOperatorIcon nodeOperator={delegatedToOperator} size="sm" />
                </Box>

                <Stack grow gap="sm">
                    <Paragraph color="default">{delegatedToOperator?.name}</Paragraph>
                    <ClipboardCopy label={shortAddress(delegatedToOperator.address)} />
                </Stack>
            </Stack>
        </Stack>
    )
}
